import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import {
  Text,
  Section,
} from '../common';
import { viewableEpisodeMetadataShape, ratingShape } from '../common/shapes';
import { largeScreen, smallScreen } from '../../../common/mixins';
import BackIcon from './BackIcon';
import { usePlayer } from '../../PlayerContext';
import { EXIT_FULL_WINDOW_AREA } from '../../../common/constants';
import platform, { OS_TYPES } from '../../../player/utils/platform';

const ANIM_DURATION = 300;
// Full visibility including animation time
const RATING_DURATION = 6000;
const RATING_SIZE = 32;

const overlayRules = ({ theme, hidden }) => ({
  display: 'flex',
  background: `linear-gradient(to bottom, ${theme.color.overlayDarkHeavy}, transparent)`,
  color: theme.color.light,
  paddingLeft: theme.margin.xxlarge,
  paddingRight: theme.margin.medium,
  paddingTop: theme.margin.xxlarge,
  width: '100%',
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 1,
  transition: `opacity ${ANIM_DURATION / 2}ms, transform ${ANIM_DURATION}ms`,
  direction: theme.origDirection,

  extend: [
    largeScreen({
      paddingLeft: theme.margin.large,
      paddingTop: theme.margin.large,
    }),
    smallScreen({
      paddingLeft: theme.margin.medium,
      paddingTop: theme.margin.medium,
    }),
    {
      condition: hidden,
      style: {
        transform: 'translateY(-100%)',
        opacity: '0',
      },
    },
  ],
});

const ratingRules = ({ theme, hideRating }) => ({
  background: `linear-gradient(to bottom, ${theme.color.overlayDarkHeavy}, transparent)`,
  color: theme.color.light,
  paddingLeft: theme.margin.xxlarge,
  paddingRight: theme.margin.medium,
  paddingTop: theme.margin.xxlarge,
  width: '100%',
  position: 'absolute',
  left: 0,
  top: 0,
  zIndex: 1,
  opacity: 0,
  transition: `opacity ${ANIM_DURATION}ms`,
  userSelect: 'none',
  direction: theme.origDirection,

  '& .ratingName': {
    display: 'inline-block',
    backgroundColor: theme.color.ratingBgColor,
    color: theme.color.ratingTextColor,
    padding: '6px 6px 5px',
    borderRadius: '2px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    lineHeight: '1.2em',
    fontSize: theme.fontSize.medium,
    minWidth: '32px',
    textAlign: 'center',
  },

  '& img': {
    height: '32px',
  },

  extend: [
    largeScreen({
      paddingLeft: theme.margin.large,
      paddingTop: theme.margin.large,
    }),
    smallScreen({
      paddingLeft: theme.margin.medium,
      paddingTop: theme.margin.medium,
    }),
    {
      condition: !hideRating,
      style: {
        opacity: 1,
        transition: `opacity ${ANIM_DURATION}ms ${ANIM_DURATION}ms`,
      },
    },
  ],
});

const backRules = ({ isMobile, theme }) => ({
  '& svg': {
    opacity: theme.opacity.enabled,
    transition: `opacity ${theme.transition.default}, transform ${theme.transition.default}`,
    willChange: 'opacity, transform',
    extend: [
      {
        condition: isMobile,
        style: {
          opacity: theme.opacity.hovered,
        },
      },
    ],
  },
  ':hover': {
    '& svg': {
      transform: 'scale(1.2) translateZ(0px)',
      opacity: theme.opacity.hovered,
    },
  },
});
const titleRules = ({ isMobile, theme }) => ({
  cursor: 'pointer',
  opacity: theme.opacity.enabled,
  transition: theme.transition.default,
  extend: [
    {
      condition: isMobile,
      style: {
        opacity: theme.opacity.hovered,
      },
    },
  ],
  ':hover': {
    opacity: theme.opacity.hovered,
  },
});

function MetadataOverlay({ episode, title, hideBackArrow, hideTitle, hidden, isBuffering, rating }) {
  const ratingTimer = useRef();
  const [hideRating, setHideRating] = useState(false);
  const [initEnds, setInitEnds] = useState(false);
  const isMobile = [OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os);
  const { css } = useFela({ hidden, hideRating: hideRating || !hidden || !initEnds, isMobile });
  const player = usePlayer();

  useEffect(() => {
    // if menu is hidden, show rating
    if (hidden && !hideRating && initEnds) {
      ratingTimer.current = setTimeout(() => {
        setHideRating(true);
      }, RATING_DURATION);
    }

    return () => clearTimeout(ratingTimer.current);
  }, [hidden, initEnds]);

  useEffect(() => {
    if (!isBuffering && !initEnds) {
      setInitEnds(true);
    }
  }, [isBuffering]);

  return (
    <>
      {rating && (
        <div className={css(ratingRules)}>
          {rating.image ? (
            <img src={`${rating.image}?height=${RATING_SIZE * 2}`} height={RATING_SIZE} alt={rating.name} />
          ) : (
            <div className="ratingName">{rating.name}</div>
          )}
          {!!rating?.reasons?.length && (
            <Section marginTop="fine" marginBottom="small">
              <Text fontSize="normal">{rating.reasons.join(', ')}</Text>
            </Section>
          )}
        </div>
      )}

      <div className={css(overlayRules)}>
        {!hideBackArrow && (
          <BackIcon className={css(backRules)} onClick={() => player._exitFullWindow(EXIT_FULL_WINDOW_AREA.ICON)} />
        )}

        {!hideTitle && title && (
          <Section
            className={`videoTitle ${css(titleRules)}`}
            onClick={() => player._exitFullWindow(EXIT_FULL_WINDOW_AREA.TITLE)}
            margin="0px"
          >
            <Text bold fontSize="medium" lineHeight="1.4">
              {title}
            </Text>
            {episode ? (
              <Section className="videoEpisode" marginTop="fine" marginBottom="small">
                <Text
                  id="season"
                  values={{
                    seasonNumber: episode.seasonNumber,
                  }}
                  fontSize="normal"
                />
                <span> | </span>
                <Text
                  id="episode"
                  values={{
                    episodeNumber: episode.episodeNumber,
                  }}
                  fontSize="normal"
                />
              </Section>
            ) : null}
          </Section>
        )}
      </div>
    </>
  );
}

MetadataOverlay.propTypes = {
  episode: viewableEpisodeMetadataShape,
  title: PropTypes.string,
  hideBackArrow: PropTypes.bool,
  hideTitle: PropTypes.bool,
  hidden: PropTypes.bool,
  isBuffering: PropTypes.bool,
  rating: ratingShape,
};

export default React.memo(MetadataOverlay);
